import { Link } from "react-router-dom";
import { ArrowRight, Download, ChevronLeft } from "lucide-react";
import SEO from "../seo/SEO";
import { ORGANIZATION_SCHEMA, buildBreadcrumbSchema } from "../seo/seoConfig";

const heading = { fontFamily: "'Satoshi', sans-serif" };

const SAMPLES = {
  invoice: {
    title: "Sample Tax Invoice (GST Format)",
    description: "Sample Indian Tax Invoice format compliant with GST 2025 — all 16 mandatory fields, HSN summary, CGST/SGST split. Free template.",
    path: "/sample/invoice",
    docNo: "INV/2026-27/0042",
    docDate: "28 / April / 2026",
    label: "Tax Invoice",
    type: "invoice",
  },
  quotation: {
    title: "Sample Quotation Format India",
    description: "Sample Quotation format for Indian businesses — line items, GST, validity, terms, signature. Free downloadable template.",
    path: "/sample/quotation",
    docNo: "Q/2026-27/0017",
    docDate: "28 / April / 2026",
    label: "Quotation",
    type: "quotation",
  },
};

export default function SamplePage({ doc = "invoice" }) {
  const sample = SAMPLES[doc] || SAMPLES.invoice;

  return (
    <div className="bg-white min-h-screen" data-testid={`sample-${doc}`}>
      <SEO
        title={sample.title}
        description={sample.description}
        path={sample.path}
        schemas={[
          ORGANIZATION_SCHEMA,
          buildBreadcrumbSchema([
            { name: "Home", url: "/" },
            { name: "Samples", url: "/sample/invoice" },
            { name: sample.title, url: sample.path },
          ]),
        ]}
      />

      <div className="bg-white/90 backdrop-blur border-b border-slate-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900">
            <ChevronLeft className="w-4 h-4" /> Back to home
          </Link>
          <Link to="/auth" className="bg-blue-600 text-white font-semibold rounded-lg px-4 py-1.5 hover:bg-blue-700 text-xs sm:text-sm" data-testid="sample-cta-header">
            Create yours free
          </Link>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <h1 className="text-3xl sm:text-4xl font-black text-slate-900 mb-2" style={heading}>{sample.title}</h1>
        <p className="text-slate-600 mb-6">{sample.description}</p>

        {/* Visual sample doc */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-[0_24px_60px_rgba(0,0,0,0.08)] overflow-hidden">
          <div className="p-6 sm:p-10">
            {/* Header */}
            <div className="text-center pb-5 border-b-2 border-slate-200">
              <p className="text-xs uppercase tracking-[0.18em] font-bold text-slate-400">{sample.label}</p>
              <h2 className="text-2xl font-bold text-slate-900 mt-1" style={heading}>Acme Trading Co. Pvt. Ltd.</h2>
              <p className="text-xs text-slate-500 mt-1">12, Industrial Estate, Pune, Maharashtra 411019 · GSTIN 27AABCU9603R1ZP</p>
            </div>

            {/* Doc meta */}
            <div className="grid sm:grid-cols-2 gap-5 mt-6">
              <div>
                <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1">Bill To</p>
                <p className="text-sm font-semibold text-slate-900">M/s Shree Construction Co.</p>
                <p className="text-xs text-slate-600 mt-0.5">Plot 47, MIDC, Aurangabad, MH 431005</p>
                <p className="text-xs text-slate-500 font-mono mt-0.5">GSTIN: 27ABCDE5678P1Z9</p>
              </div>
              <div className="sm:text-right">
                <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1">{sample.label} No.</p>
                <p className="text-sm font-mono font-bold text-slate-900">{sample.docNo}</p>
                <p className="text-[11px] text-slate-500 mt-2">Date: {sample.docDate}</p>
                {doc === "quotation" && <p className="text-[11px] text-slate-500">Valid Until: 28 / May / 2026</p>}
              </div>
            </div>

            {/* Items table */}
            <table className="w-full text-sm mt-6 border-t border-b border-slate-200">
              <thead>
                <tr className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                  <th className="text-left py-2.5 pr-2">#</th>
                  <th className="text-left py-2.5 pr-2">Item / Description</th>
                  <th className="text-left py-2.5 pr-2">HSN</th>
                  <th className="text-right py-2.5 pr-2">Qty</th>
                  <th className="text-right py-2.5 pr-2">Rate</th>
                  <th className="text-right py-2.5 pr-2">CGST</th>
                  <th className="text-right py-2.5 pr-2">SGST</th>
                  <th className="text-right py-2.5">Amount</th>
                </tr>
              </thead>
              <tbody className="text-sm text-slate-800">
                {[
                  { name: "Steel TMT Bars 12mm", hsn: "7214", qty: "500 kg", rate: "58.00", cgst: "2,610", sgst: "2,610", amt: "34,220" },
                  { name: "Ambuja Cement 53 Grade", hsn: "2523", qty: "200 bag", rate: "380.00", cgst: "6,840", sgst: "6,840", amt: "89,680" },
                  { name: "Birla White Putty 40 kg", hsn: "3214", qty: "50 bag", rate: "820.00", cgst: "3,690", sgst: "3,690", amt: "48,380" },
                ].map((r, i) => (
                  <tr key={i} className="border-t border-slate-100">
                    <td className="py-2.5 pr-2 text-slate-400">{i + 1}</td>
                    <td className="py-2.5 pr-2 font-medium">{r.name}</td>
                    <td className="py-2.5 pr-2 text-slate-500 font-mono text-xs">{r.hsn}</td>
                    <td className="py-2.5 pr-2 text-right">{r.qty}</td>
                    <td className="py-2.5 pr-2 text-right">{r.rate}</td>
                    <td className="py-2.5 pr-2 text-right text-xs text-slate-500">{r.cgst}</td>
                    <td className="py-2.5 pr-2 text-right text-xs text-slate-500">{r.sgst}</td>
                    <td className="py-2.5 text-right font-semibold">{r.amt}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Totals */}
            <div className="flex justify-end mt-5">
              <div className="w-full sm:w-72 space-y-1.5 text-sm">
                <div className="flex justify-between text-slate-600"><span>Subtotal (Excl. Tax)</span><span>1,49,000</span></div>
                <div className="flex justify-between text-slate-600"><span>CGST</span><span>13,140</span></div>
                <div className="flex justify-between text-slate-600"><span>SGST</span><span>13,140</span></div>
                <div className="flex justify-between pt-2 border-t-2 border-slate-200 text-base font-bold text-slate-900">
                  <span>Grand Total</span><span className="text-blue-600">₹ 1,75,280</span>
                </div>
              </div>
            </div>

            <p className="text-[11px] text-slate-500 mt-6 italic">Amount in Words: One Lakh Seventy Five Thousand Two Hundred Eighty Rupees Only</p>

            <div className="mt-8 pt-5 border-t border-slate-100 flex flex-col sm:flex-row justify-between gap-3">
              <div>
                <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1">Payment Terms</p>
                <p className="text-xs text-slate-600">Due within 30 days of invoice date</p>
              </div>
              <div className="text-center sm:text-right">
                <p className="text-[11px] text-slate-400 mb-6">For Acme Trading Co.</p>
                <p className="text-xs text-slate-500 italic">Authorised Signatory</p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-blue-50 border border-blue-100 rounded-2xl p-6 mt-8 text-center">
          <h3 className="font-black text-slate-900 mb-1.5 text-lg" style={heading}>Like this sample?</h3>
          <p className="text-sm text-slate-600 mb-4">Generate the same {sample.label.toLowerCase()} for your business in 30 seconds — fully editable PDF.</p>
          <Link
            to="/auth"
            data-testid="sample-cta-bottom"
            className="inline-flex items-center gap-2 bg-blue-600 text-white font-semibold rounded-lg px-6 py-3 hover:bg-blue-700 transition-colors"
          >
            Create your free {sample.label.toLowerCase()} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        <div className="mt-10 grid sm:grid-cols-2 gap-4 text-sm">
          <Link to="/sample/invoice" className="bg-white border border-slate-200 rounded-xl p-4 hover:border-blue-300 transition-colors">
            <p className="font-semibold text-slate-900">Sample Tax Invoice</p>
            <p className="text-xs text-slate-500 mt-0.5">Indian GST format with HSN summary and signature.</p>
          </Link>
          <Link to="/sample/quotation" className="bg-white border border-slate-200 rounded-xl p-4 hover:border-blue-300 transition-colors">
            <p className="font-semibold text-slate-900">Sample Quotation</p>
            <p className="text-xs text-slate-500 mt-0.5">Pre-sales format with validity, terms and signature.</p>
          </Link>
        </div>
      </div>

      <footer className="py-8 px-4 sm:px-6 border-t border-slate-100 bg-white text-center text-xs text-slate-400 mt-8">
        Powered by <Link to="/" className="text-blue-600 font-semibold">InvoiceSetu</Link> — India’s smart invoice software for small businesses.
      </footer>
    </div>
  );
}
